import { useNavigation } from "@react-navigation/native";
import {
  ChevronRight,
  ChevronUp,
  Plus,
  TriangleAlert,
} from "@tamagui/lucide-icons";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import React, { type PropsWithChildren, useState } from "react";
import { Text, XStack, YStack } from "tamagui";
import { PrimaryCheck } from "../../../core/components/PrimaryCheck";
import { useStores } from "../../../core/stores";
import { sanitizeForFirestore } from "../../../modules/core/sanitizeForFirestore";
import { Permission } from "../../../permissions/types";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Group } from "../../../ui/Group";
import { Label } from "../../../ui/Label";
import { IconPill, PillType } from "../../../ui/Pill";
import { Subtext } from "../../../ui/Subtext";
import { useLoadBusinessPlanModules } from "../../modules/ui/BusinessTasksList";
import { BusinessScheduleItemForm } from "../../schedule/BusinessScheduleItemForm";
import {
  type Event,
  type EventHeader,
  type EventModule,
  type EventTeamModule,
  type EventToday,
  getEventEndTime,
  getEventStartTime,
  isEvent,
  isEventModule,
  isEventTeamModule,
  isEventToday,
} from "../../scheduleTypes";
import type { BusinessPlanModel } from "../BusinessPlanModel";
import { useScheduleItems } from "./useScheduleItems";

type BusinessPlanScheduleProps = {
  businessPlanUid: string;
};

const HeaderRow: React.FC<{ header: EventHeader }> = observer(({ header }) => {
  return (
    <Label textAlign="center" width="100%">
      {header.title}
    </Label>
  );
});

const Row: React.FC<
  PropsWithChildren<{
    showDay?: boolean;
    subtext: string;
    item: Event | EventModule | EventTeamModule;
    businessPlan: BusinessPlanModel;
  }>
> = observer(({ businessPlan, item, showDay, children, subtext }) => {
  const navigation = useNavigation();

  return (
    <XStack
      cursor="pointer"
      onPress={() =>
        navigation.navigate("BusinessScheduleItem", {
          businessUid: businessPlan.uid,
          itemUid: item.uid,
        })
      }
      width={"100%"}
    >
      <Label width={80} flexShrink={0}>
        {showDay ? dayjs(item.date).format("ddd DD") : ""}
      </Label>
      <YStack flex={1} gap="$small">
        {children}

        <Subtext>{subtext}</Subtext>
      </YStack>
      <XStack flexShrink={0} gap="$small">
        <ChevronRight />
      </XStack>
    </XStack>
  );
});

const ItemRow: React.FC<{
  showDay?: boolean;
  businessPlan: BusinessPlanModel;
  item: Event;
}> = observer((props) => {
  const startTime = getEventStartTime(props.item);
  const endTime = getEventEndTime(props.item);

  return (
    <Row {...props} subtext="Event">
      <Text>
        {startTime ? (
          <Label>
            {startTime}
            {endTime ? ` - ${endTime}` : ""}{" "}
          </Label>
        ) : undefined}
        {props.item.title}
      </Text>
    </Row>
  );
});

const ModuleRow: React.FC<{
  showDay?: boolean;
  businessPlan: BusinessPlanModel;
  item: EventModule;
}> = observer((props) => {
  const isDone = props.item.module.isCompletedByUserOrTeam;
  const dueDate = props.item.module.calculatedDate;
  const isLate = !isDone && Date.now() > dueDate.toMillis();
  const textColor = isLate ? "$danger" : undefined;

  return (
    <Row {...props} subtext="Task">
      <XStack flex={1} gap="$small">
        {isLate ? (
          <TriangleAlert size={22} flexShrink={0} color={textColor} />
        ) : undefined}
        {isDone ? <PrimaryCheck flexShrink={0} /> : undefined}
        <Body color={textColor}>{props.item.title}</Body>
      </XStack>
    </Row>
  );
});

const TeamModuleRow: React.FC<{
  showDay?: boolean;
  businessPlan: BusinessPlanModel;
  item: EventTeamModule;
}> = observer((props) => {
  const isDone = props.item.module.isCompletedByUser;
  const dueDate = props.item.module.data.dateDue;
  const isLate = !isDone && Date.now() > dueDate.toMillis();
  const textColor = isLate ? "$danger" : undefined;

  return (
    <Row {...props} subtext="Team">
      <XStack gap="$small" alignItems="center">
        {isLate ? (
          <TriangleAlert size={22} flexShrink={0} color={textColor} />
        ) : undefined}
        {isDone ? <PrimaryCheck flexShrink={0} /> : undefined}
        <Body color={textColor}>{props.item.title}</Body>
      </XStack>
    </Row>
  );
});

const TodayRow: React.FC<{ item: EventToday }> = observer(({ item }) => {
  return (
    <XStack opacity={0.5} width={"100%"}>
      <Label width={80} flexShrink={0}>
        {dayjs(item.date).format("ddd DD")}
      </Label>
      <Body>Today</Body>
    </XStack>
  );
});

const AddEventForm: React.FC<{ businessPlan: BusinessPlanModel }> = observer(
  ({ businessPlan }) => {
    const [showForm, setShowForm] = useState(false);

    if (!showForm) {
      return (
        <>
          {businessPlan.checkPermission(Permission.CanEditSchedules) && (
            <IconPill
              text="Add event"
              icon={Plus}
              type={PillType.Primary}
              onPress={() => setShowForm(true)}
            />
          )}
        </>
      );
    }

    return (
      <BusinessScheduleItemForm
        businessPlanStartingDate={businessPlan.data.eventInfo.date}
        onSave={async (data) => {
          if (!(data.date || data.offsetInDays) || !data.title) {
            return;
          }

          await businessPlan.addScheduleItem({
            date: data.date ?? null,
            offsetInDays: data.offsetInDays ?? null,
            emoji: data.emoji ?? null,
            title: data.title,
            description: sanitizeForFirestore(data.description ?? null),
            schedule: data.schedule ?? null,
          });

          setShowForm(false);
        }}
      />
    );
  },
);

export const BusinessPlanScheduleUI = observer(
  ({ businessPlanUid }: BusinessPlanScheduleProps) => {
    const { businessPlansStore } = useStores();
    const [showPast, setShowPast] = useState(false);

    const { groupedList, hasPastEvents } = useScheduleItems(
      businessPlanUid,
      showPast,
    );
    const businessPlan = businessPlansStore.byId.get(businessPlanUid);

    useLoadBusinessPlanModules(businessPlan);

    if (!businessPlan) {
      return null;
    }

    const showPastExpander = hasPastEvents && !showPast;

    let previousDay: string | undefined;

    return (
      <>
        <AddEventForm businessPlan={businessPlan} />
        <Box>
          {showPastExpander && (
            <XStack
              cursor="pointer"
              onPress={() => setShowPast(true)}
              alignItems="center"
              justifyContent="center"
              gap="$small"
            >
              <ChevronUp /> Show past
            </XStack>
          )}
          <Group borderTop={showPastExpander}>
            {groupedList.map((item) => {
              const showDay = "date" in item && previousDay !== item.date;
              previousDay = "date" in item ? item.date : previousDay;

              if (isEvent(item)) {
                return (
                  <ItemRow
                    showDay={showDay}
                    businessPlan={businessPlan}
                    key={item.uid}
                    item={item}
                  />
                );
              }

              if (isEventModule(item)) {
                return (
                  <ModuleRow
                    showDay={showDay}
                    key={item.uid}
                    businessPlan={businessPlan}
                    item={item}
                  />
                );
              }
              if (isEventTeamModule(item)) {
                return (
                  <TeamModuleRow
                    showDay={showDay}
                    key={item.uid}
                    businessPlan={businessPlan}
                    item={item}
                  />
                );
              }

              if (isEventToday(item)) {
                return <TodayRow key={"today"} item={item} />;
              }

              return <HeaderRow key={item.title} header={item} />;
            })}
          </Group>
        </Box>
      </>
    );
  },
);
